// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Typography } from '@mui/material';
import { AutocompleteInput, AppIcon } from '@shared/ui-kit/components';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { FundSearchResult } from '@shared/portfolio-operations/types';
import { useFundSearchInput } from './use-fund-search-input';

export interface FundSearchInputProps {
  onSelectFund: (fund: FundSearchResult) => void;
  excludedTickers: string[];
  portfolioFunds?: FundSearchResult[];
}

export const FundSearchInput = defineComponent({
  displayName: 'FundSearchInput',
  useSetup: useFundSearchInput,
  View: ({
    onSelectFund,
    searchValue,
    onSelectValue,
    onSelectDetails,
    filteredPortfolioFunds,
    t,
    excludedTickers,
  }) => (
    <div className="space-y-2">
      <div className="relative">
        <AutocompleteInput
          error={false}
          onChangeValue={onSelectValue}
          value={searchValue}
          name="fund-search"
          onSelectValue={onSelectValue}
          onChangeDetails={onSelectDetails}
          noMargin
          data-testid="fund-search-input"
          assetType="mutual_funds"
          leftIcon={<AppIcon icon="Search" color="disabled" size="base" />}
          placeholder={t('ticker-modal.search-funds', { ns: 'components' })}
          excludedTickers={excludedTickers}
        />
      </div>

      {filteredPortfolioFunds && filteredPortfolioFunds.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded-lg border bg-white">
          {filteredPortfolioFunds.map(fund => (
            <div
              key={fund.ticker}
              onClick={() => onSelectFund(fund)}
              className="flex cursor-pointer items-center justify-between border-b border-gray-100 px-3 py-2 hover:bg-gray-50"
              data-testid={`portfolio-fund-${fund.ticker}`}
            >
              <Typography variant="body2" className="font-medium">
                {fund.name}
              </Typography>
              {fund.isin && (
                <Typography variant="caption" color="text.secondary">
                  {fund.isin}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  ),
});
